import { useState, useEffect, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { ChevronRight } from "lucide-react";
import remarkGfm from "remark-gfm";
import { ToolStatusIcon } from "~/components/assistant-v2/ToolStatusIcon";
import { CopyButton } from "~/components/assistant/CopyButton";
import { MARKDOWN_COMPONENTS } from "~/components/assistant/lib/markdownComponents";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";

export const CreatePlanMessage: ToolCallMessagePartComponent = (props) => {
  const { argsText, result, status } = props;

  const isCancelled = status?.type === "incomplete" && status.reason === "cancelled";

  const [isExpanded, setIsExpanded] = useState(status?.type === "running");

  useEffect(() => {
    if (status?.type === "running") {
      setIsExpanded(true);
    } else if (status?.type === "complete") {
      setIsExpanded(false);
    }
  }, [status?.type]);

  const parsedArgs = useMemo(() => {
    if (!argsText) return null;
    try {
      return JSON.parse(argsText);
    } catch {
      return null;
    }
  }, [argsText]);

  const resultContent = useMemo(() => {
    if (!result) return null;
    if (typeof result === "string") return result;
    if (typeof result === "object" && result !== null && "raw" in result) {
      return (result as { raw: string }).raw;
    }
    return JSON.stringify(result, null, 2);
  }, [result]);

  const hasResult = resultContent && resultContent.trim().length > 0;

  return (
    <div className="border border-gray-200 rounded-lg p-2 mb-2">
      <details
        className="space-y-3"
        open={isExpanded}
        onToggle={(e) => {
          const target = e.target as HTMLDetailsElement;
          setIsExpanded(target.open);
          e.stopPropagation();
        }}
      >
        <summary className="list-none cursor-pointer mb-0 select-none">
          <div className="flex items-center gap-1.5 justify-between">
            <div className="flex gap-2 items-center">
              <ToolStatusIcon status={status} />
              <span className="text-xs font-medium text-purple-600">
                Create Plan
                {isCancelled && " (cancelled)"}
              </span>
            </div>
            <ChevronRight
              className={cn(
                "size-4 text-purple-600 transition-transform duration-200",
                isExpanded && "rotate-90"
              )}
            />
          </div>
        </summary>

        <div className="p-2 pb-0">
          {!hasResult ? (
            <div className="group relative pt-1">
              <CopyButton
                text={JSON.stringify(parsedArgs ?? argsText, null, 2)}
                variant="light"
                size="sm"
                className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
              />
              <DetailField content={parsedArgs ?? argsText ?? ""} />
            </div>
          ) : (
            <Tabs defaultValue="output" className="gap-0">
              <TabsList variant="underlined" className="mb-2">
                <TabsTrigger value="output" className="text-xs py-1.5 pb-2 mr-4">
                  Output
                </TabsTrigger>
                <TabsTrigger value="input" className="text-xs py-1.5 pb-2">
                  Input
                </TabsTrigger>
              </TabsList>

              <TabsContent value="output">
                <div className="group relative">
                  <CopyButton
                    text={resultContent}
                    variant="light"
                    size="sm"
                    className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                  <div className="prose prose-sm max-w-none text-sm">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MARKDOWN_COMPONENTS}>
                      {resultContent}
                    </ReactMarkdown>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="input">
                <div className="group relative">
                  <CopyButton
                    text={JSON.stringify(parsedArgs ?? argsText, null, 2)}
                    variant="light"
                    size="sm"
                    className="absolute top-1 right-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                  <DetailField content={parsedArgs ?? argsText ?? ""} />
                </div>
              </TabsContent>
            </Tabs>
          )}
        </div>
      </details>
    </div>
  );
};
